import type { UserData } from '@core/state/user.store';

import { capabilitiesForRoles, normalizeRoles, type Capability, type PortalRole } from './capabilities';
import { FEATURES, FEATURE_IDS, type FeatureDefinition, type FeatureId } from './features';
import { capabilitiesFromServer } from './server-access';
import { ALL_FLAGS_ON, DEFAULT_FLAGS, capabilitiesSwitchedOff, type FlagId } from './flags';

/**
 * What this user may do, resolved once and read everywhere.
 *
 * The resolution goes through a provider so the answer can later come from the
 * server without any call site changing. Today the only provider is the local
 * one below: roles in, capabilities out, using the table in `capabilities.ts`.
 * When `GET /api/v1/me` has answered, `user.access` carries the server's
 * permissions and they decide instead (see `server-access.ts`).
 *
 * The signed-out case is a real case, not an edge one. Claims arrive after the
 * first render, and a resolver that treated "not known yet" as "denied" would
 * flash a stripped-down shell at every user on every cold load. `pending` says
 * which of the two it is, and the shell waits rather than guessing.
 *
 * Feature flags are applied after the provider, whichever provider it is: a
 * switched-off flag takes its capabilities away from everybody, so a server
 * that grants `assignments.bulk` still cannot put Batches back on screen while
 * the `bulkBatches` flag is off.
 */

/* Every product surface is also a flag of the same name; this fails to compile if one is not. */
const FEATURES_ARE_FLAGS: readonly FlagId[] = FEATURE_IDS;
void FEATURES_ARE_FLAGS;

export interface Entitlements {
  /** The user's roles after aliases and implications are applied. */
  roles: Set<PortalRole>;
  capabilities: Set<Capability>;
  /** The feature flags switched on. Their capabilities are already removed from `capabilities`. */
  flags: ReadonlySet<FlagId>;
  /** True while sign-in has not resolved: nothing is granted, nothing is denied. */
  pending: boolean;
}

export const EMPTY_ENTITLEMENTS: Entitlements = {
  roles: new Set<PortalRole>(),
  capabilities: new Set<Capability>(),
  flags: DEFAULT_FLAGS,
  pending: true
};

export type EntitlementsProvider = (user: UserData | null | undefined) => Entitlements;

/** Roles in, capabilities out. Everything it needs is in the claims. */
const localProvider: EntitlementsProvider = (user) => {
  if (!user) {
    return EMPTY_ENTITLEMENTS;
  }

  const roles = normalizeRoles(user.roles);

  // The server's answer wins once it has arrived: permissions resolved from
  // group mappings, per the RBAC design. Claims are the fallback, not a union.
  if (user.access) {
    return { roles, capabilities: capabilitiesFromServer(user.access, user.roles), flags: ALL_FLAGS_ON, pending: false };
  }

  return { roles, capabilities: capabilitiesForRoles(roles), flags: ALL_FLAGS_ON, pending: false };
};

let provider: EntitlementsProvider = localProvider;

/**
 * Swaps the resolver - for the future server-backed one, and for tests that
 * need a user with a specific grant without constructing claims for it.
 */
export function setEntitlementsProvider(next: EntitlementsProvider | null): void {
  provider = next ?? localProvider;
}

/**
 * The user's entitlements with the switched-on flags applied. `flags` defaults
 * to the registry defaults; the app passes the resolved set from the flag store.
 */
export function resolveEntitlements(
  user: UserData | null | undefined,
  flags: ReadonlySet<FlagId> = DEFAULT_FLAGS
): Entitlements {
  return applyFlags(provider(user), flags);
}

/** Takes away whatever the switched-off flags gate, and records which flags are on. */
export function applyFlags(entitlements: Entitlements, flags: ReadonlySet<FlagId>): Entitlements {
  const off = capabilitiesSwitchedOff(flags);

  if (off.size === 0) {
    return { ...entitlements, flags };
  }

  const capabilities = new Set([...entitlements.capabilities].filter((capability) => !off.has(capability)));

  return { ...entitlements, capabilities, flags };
}

/**
 * Entitlements from bare role names, bypassing the provider.
 *
 * For the two places that hold roles without holding a user: the in-memory
 * transport, which plays the part of the API and is handed the caller's roles,
 * and tests that want "an Analyst" without building a set of claims to get
 * one. Screens do not use this - they use `useEntitlements`.
 *
 * Every flag is on unless `flags` says otherwise: the in-memory transport
 * plays the API, and the API knows nothing about what the client has switched off.
 */
export function entitlementsFor(
  roles: readonly string[] | undefined,
  flags: ReadonlySet<FlagId> = ALL_FLAGS_ON
): Entitlements {
  const normalized = normalizeRoles(roles);

  return applyFlags(
    { roles: normalized, capabilities: capabilitiesForRoles(normalized), flags, pending: false },
    flags
  );
}

/* -------------------------------------------------------------- predicates -- */

export function can(entitlements: Entitlements, capability: Capability | null | undefined): boolean {
  if (!capability) {
    return !entitlements.pending;
  }

  return entitlements.capabilities.has(capability);
}

export function canAny(entitlements: Entitlements, capabilities: readonly Capability[]): boolean {
  return capabilities.some((capability) => entitlements.capabilities.has(capability));
}

/** Whether a flag is switched on. For the switches with no capability behind them. */
export function isFlagOn(entitlements: Entitlements, flag: FlagId): boolean {
  return entitlements.flags.has(flag);
}

/** Switched on, and held. Help has no capability, so for it the flag is the whole answer. */
export function hasFeature(entitlements: Entitlements, feature: FeatureId): boolean {
  return entitlements.flags.has(feature) && can(entitlements, FEATURES[feature].capability);
}

/** The features this user holds, for the nav and for a landing-route decision. */
export function visibleFeatures(entitlements: Entitlements): FeatureDefinition[] {
  if (entitlements.pending) {
    return [];
  }

  return Object.values(FEATURES).filter((feature) => hasFeature(entitlements, feature.id));
}
